import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Hover tooltip with portal-rendered floating bubble. Use this anywhere
// native `title=` falls short — multi-line copy, formatted content, or
// rich hover affordances (badge details, locked-toggle reasons).
//
// Usage:
//   <Tooltip content="Why this is locked">
//     <button>…</button>
//   </Tooltip>
//
// The component clones into a span wrapper so the consumer doesn't have
// to add a position-relative parent. The bubble itself portals to body
// and positions on mount + on every show, so it dodges container clip
// (overflow: hidden on a parent row would otherwise hide the tip).
//
// Position is "top" by default — bubble sits above the trigger with an
// arrow pointing down. Pass `position="bottom"` for below-trigger.
//
// Triggering: hover (mouseenter / mouseleave) AND focus (focusin /
// focusout) so keyboard users see the same content.
export default function Tooltip({
  content,
  position = 'top',
  delay = 100,
  children,
  disabled = false,
}) {
  const triggerRef = useRef(null);
  const showTimeoutRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ left: 0, top: 0 });

  // Recompute bubble position from the trigger's bounding rect. Called
  // when we open + whenever the window resizes/scrolls so a tooltip held
  // open during page motion doesn't drift off its anchor.
  const reposition = () => {
    const t = triggerRef.current;
    if (!t) return;
    const rect = t.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = position === 'top' ? rect.top : rect.bottom;
    setCoords({ left: x, top: y });
  };

  useEffect(() => {
    if (!open) return;
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const show = () => {
    if (disabled || !content) return;
    if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
    showTimeoutRef.current = setTimeout(() => setOpen(true), delay);
  };
  const hide = () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    setOpen(false);
  };

  useEffect(() => () => {
    if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
  }, []);

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        style={{ display: 'inline-flex' }}>
        {children}
      </span>
      {open && content && createPortal(
        <div
          role="tooltip"
          style={{
            position: 'fixed',
            left: coords.left,
            top: coords.top,
            transform: position === 'top'
              ? 'translate(-50%, calc(-100% - 8px))'
              : 'translate(-50%, 8px)',
            zIndex: 250,
            background: '#2a2a2a',
            border: '1px solid #3a3a3a',
            borderRadius: 4,
            padding: '8px 10px',
            fontFamily: 'Manrope, sans-serif',
            fontSize: 11,
            lineHeight: 1.4,
            color: '#F9FCF1',
            maxWidth: 260,
            boxShadow: '0 8px 20px rgba(0,0,0,0.4)',
            pointerEvents: 'none',
            whiteSpace: 'normal',
          }}>
          {content}
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              [position === 'top' ? 'top' : 'bottom']: '100%',
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              [position === 'top' ? 'borderTop' : 'borderBottom']: '5px solid #2a2a2a',
            }} />
        </div>,
        document.body
      )}
    </>
  );
}
